<template lang="pug">
LazyBookingCountToast(v-if="enableToast")
VeeField(
  name="pickupLocation"
  :validate-on-blur="false"
  v-slot="{ field, errors }")
  PlacesSearchRefined(
    ref="pickupInput"
    v-bind="field"
    :model-value="getPickupAddress !== null ? getPickupAddress.place.formatted_address : ''"
    @focus="onFocus"
    @blur="onBlur"
    @place="setPickupPlace"
    input-id="pickup-location"
    :custom-classes="customClasses"
    :label="$t('location_form.moving_from')"
    :placeholder="labelPosition === 'animated' ? undefined : $t('location_form.town_or_postcode')"
    :label-position="labelPosition"
    label-bg="white"
    qa-tag="inp-pickup-loc")
  VeeErrorMessage(name="pickupLocation")
    TextInputError.my-ds-sm
      | {{ $t("location_form.error.moving_from") }}
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { Toast } from "@anyvan-ui/toast"
import { mapGetters, mapActions } from "vuex"
import { getAttributes } from "~/utils/block"
import {
  SET_PICKUP_ADDRESS,
  SET_PICKUP_FLOOR,
  SET_LISTING,
  GET_PICKUP_ADDRESS,
  state as InitialListing,
  defaultAddress
} from "~/store/listing"
import { placeToAddress } from "~/scripts/convert"
import { isSameLocaleAsPlacesLocale }  from "~/scripts/useful-functions"
import { FETCH_BOOKINGS_COUNT } from "~/store/booking-insights"

export default defineComponent({
  name: "NewLocationPickupInput",
  components: {
    Toast
  },

  props: {
    block: {
      type: Object as () => Block,
      required: true
    },
    virgin: {
      type: Boolean
    },
    lazy: {
      type: Boolean,
      default: true
    },
    enableToast: {
      type: Boolean,
      default: false
    },
    deliveryInput: {
      type: Object,
      default: undefined
    },
    customClasses: {
      type: String,
      default: ""
    },
    labelPosition: {
      type: String,
      default: "border"
    }
  },
  emits: ["focus", "place"],

  computed: {
    ...mapGetters("listing", {
      getPickupAddress: GET_PICKUP_ADDRESS
    }),

    attributes(): Record<string, any> | null {
      return this.virgin ? null : this.getAttributes(this.block)
    }
  },

  created() {
    this.setListing(InitialListing())
  },

  methods: {
    ...mapActions("listing", {
      setPickupAddress: SET_PICKUP_ADDRESS,
      setPickupFloor: SET_PICKUP_FLOOR,
      setListing: SET_LISTING
    }),
    ...mapActions("booking-insights", {
      fetchBookingsCount: FETCH_BOOKINGS_COUNT
    }),

    getAttributes,

    async setPickupPlace(place: google.maps.places.PlaceResult | null): Promise<void> {
      this.$emit("place")
      this.setPickupAddress(place !== null ? placeToAddress(place) : defaultAddress)
      if (place === null) {
        this.setPickupFloor('Ground floor')
      }

      if (place && place.formatted_address) {
        const payloadForCheck = {
          pickup_address_data: {
            latitude: this.getPickupAddress.coordinates.lat,
            longitude: this.getPickupAddress.coordinates.lng
          }
        }
        if (
          this.enableToast &&
          isSameLocaleAsPlacesLocale(this.$i18n.locale, this.getPickupAddress.country) &&
          !(await isAnExcludedIsland(this.$i18n.locale, payloadForCheck)) &&
          this.getPickupAddress.city
        ) {
          try {
            const params = {
              postalCode: undefined,
              lat: this.getPickupAddress.coordinates.lat,
              lng: this.getPickupAddress.coordinates.lng,
              category: 2,
              ignoreDuration: true
            }
            if (this.getPickupAddress.postcode) {
              params.postalCode = this.getPickupAddress.postcode
            } else {
              params.lat = this.getPickupAddress.coordinates.lat
              params.lng = this.getPickupAddress.coordinates.lng
            }
            const payload = {
              $axios: this.$axios,
              $config: this.$config.public,
              params,
              originalPostalCode: ""
            }
            this.fetchBookingsCount(payload)
          } catch (error: any) {
            console.error("Failed to fetch booking count:", error)
            this.countData = null

            this.$snowplow?.trackEvent({
              category: "booking_count_api",
              action: "error",
              property: error.message || "Unknown error"
            })
          }
        }
        this.$snowplow.trackEvent({
          category: "moving_from",
          action: "selected",
          property: place.formatted_address
        })
      }

      if (place !== null && this.deliveryInput) {
        this.deliveryInput.focus()
      }
    },

    onFocus(): void {
      this.$emit("focus")
      this.$snowplow.trackEvent({
        category: "moving_from",
        action: "click"
      })
    },
    onBlur(): void {
      // Access the input element using the ref
      const inputElement = (this.$refs.pickupInput as Vue)?.$el.querySelector(
        "input"
      ) as HTMLInputElement

      if (!inputElement) {
        return
      }

      const inputValue = inputElement.value

      if (inputValue) {
        this.$snowplow.trackEvent({
          category: "moving_from",
          action: "entered",
          property: inputValue
        })
      }
    }
  }
})
</script>
